import {
  Component,
  OnDestroy,
  Input,
  ViewEncapsulation,
  OnInit,
} from "@angular/core";

import { Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { Store, select } from "@ngrx/store";

import {
  getPropertiesForListByUsage,
  getSiteProductList,
  getSiteWarehouseList,
  getWarehouseTypesList,
} from "src/app/store/actions/sites/sites.actions";
import {
  AppState,
  warehouseTypesLoadedState,
  siteWarehousesLoadedState,
  loadedDeviceWarehousePropertiesListSiteState,
  siteProductListLoadedState,
} from "src/app/store/reducers";
import {
  SiteWarehouseDTO,
  WarehouseType,
} from "src/app/models/Sites/siteWarehouseDTO";

import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { WarehouseFormDialogComponent } from "./dialog/warehouse-form-dialog.component";
import { MatDialog } from "@angular/material/dialog";

const USAGE_CODE = "DEVICE_WAREHOUSE";

@Component({
  selector: "site-warehouse-details",
  templateUrl: "./warehouse-details.component.html",
  styleUrls: ["./warehouse-details.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class WarehouseDetailsComponent implements OnInit, OnDestroy {
  _siteCode: string;
  
  @Input() warehouseType: string;
  @Input() set siteCode(value) {
    this._siteCode = value;
    if (this._siteCode) {
      this.loadWarehouseInfo(this._siteCode);
      this.loadSiteWarehouses(this._siteCode);
    }
  }

  //Subscriptions
  warehouseSub: Subscription;
  multiSubs: Subscription;

  //Wareouse List
  warehouseList$: Observable<SiteWarehouseDTO[]>;
  warehouseList: SiteWarehouseDTO[];

  //Warehouse Type list
  warehouseTypeList$: Observable<WarehouseType[]>;
  warehouseTypeList: WarehouseType[];

  //Site Products
  siteProducts$: Observable<SiteProductDTO[]>;
  siteProducts: SiteProductDTO[];
  siteProductsSub: Subscription;

  //Property List
  propertyList$: Observable<PropertyDTO[]>;
  propertyList: PropertyDTO[];

  currentWarehouse: SiteWarehouseDTO;
  isWarehouseSelected = false;

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog
  ) {
    this.warehouseTypeList$ = this.store.pipe(select(warehouseTypesLoadedState));
    this.warehouseList$ = this.store.pipe(select(siteWarehousesLoadedState));
    this.propertyList$ = this.store.pipe(select(loadedDeviceWarehousePropertiesListSiteState));
  }

  ngOnInit(): void {
    if (this.warehouseType == 'WET') {
      this.siteProducts$ = this.store.pipe(select(siteProductListLoadedState));
      this.loadSiteProducts(this._siteCode);  
    }
  }

  loadSiteWarehouses(code: string) {
    this.warehouseSub = this.warehouseList$.subscribe(
      (warehouses: SiteWarehouseDTO[]) => {
        this.warehouseList = warehouses?.filter(({ warehouseTypeCode }) => warehouseTypeCode === this.warehouseType );
        if (warehouses && warehouses.length && this.currentWarehouse) {
          const { code: currentWarehouseCode } = this.currentWarehouse;
          this.currentWarehouse = warehouses.find(({ code }) => code === currentWarehouseCode);
        }
        this.resetWarehouse();
      }
    );
    this.store.dispatch(getSiteWarehouseList({ 
      code, 
      pageReq: { realSize: true, offset: 0, limit: 999 },
    }));
  }

  loadWarehouseInfo(code: string) {
    this.multiSubs = zip(
      this.warehouseTypeList$.pipe(skip(1)),
      this.propertyList$.pipe(skip(1))
    ).subscribe(([warehouseTypes, properties]) => {
      if (warehouseTypes && warehouseTypes.length) {
        this.warehouseTypeList = warehouseTypes.map(
          (warehouseType: WarehouseType) => {
            return {
              code: warehouseType?.code,
              description: warehouseType?.description,
            };
          }
        );
      }

      if (properties && properties.length) {
        this.propertyList = properties;
      }
    });
    this.store.dispatch(getWarehouseTypesList(null));
    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: {
          query: "is_enabled==1",
          limit: undefined,
          offset: 0,
          realSize: true,
        },
        usageCode: USAGE_CODE,
      })
    );
  }

  loadSiteProducts(code: string) {
    this.siteProductsSub = this.siteProducts$.subscribe(
      (products: SiteProductDTO[]) => {
        this.siteProducts = products;
      }
    );
    this.store.dispatch(getSiteProductList({ code }));
  }

  getChipForeColor(color: string) {
    return getColorByBgColor(`#${color}`);
  }

  resetWarehouse() {
    this.dialog.closeAll();
    this.isWarehouseSelected = false;
    this.currentWarehouse = null;
  }

  selectWarehouse(warehouse: SiteWarehouseDTO) {
    this.isWarehouseSelected = true;
    this.currentWarehouse = warehouse;
    this.propertyList = this.propertyList ? [...this.propertyList] : null;

    this.openWarehouseFormDialog(false);
  }

  create() {
    this.isWarehouseSelected = false;
    this.currentWarehouse = null;

    this.openWarehouseFormDialog(true);
  }
  
  openWarehouseFormDialog(isCreate: boolean) {
    this.dialog.open(WarehouseFormDialogComponent, {
      autoFocus: false,
      data: {
        isCreate,
        siteCode: this._siteCode,
        warehouseType: this.warehouseType,
        currentWarehouse: this.currentWarehouse,
        warehouseTypeList: this.warehouseTypeList,
        siteProducts: this.siteProducts,
        propertyList: this.propertyList,
      }
    });
  }

  ngOnDestroy(): void {
    if (this.warehouseSub) {
      this.warehouseSub.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.siteProductsSub) {
      this.siteProductsSub.unsubscribe();
    }
  }
}
